/**
 * Dropdown menu item builder that can be rendered in ProseMirror's
 * menubar.
 *
 * @param {object} options - Parameters that should be passed to
 *   the dropdown builder
 */
export default function wrapAsProseMirrorDropdown({ makeDropdown }) {
	let resolvedItems;

	return (options) => ({
		/**
		 * Method used by ProseMirror to render the widget. It will render
		 * this widget using widgetjs `appendTo` method, using ProseMirror
		 * as a htmlCanvas. It returns an object that can be used by
		 * ProseMirror (i.e., containing a dom field of this widget and an
		 * update field of its behavior during ProseMirror's view update).
		 *
		 * @param {object} view - ProseMirror's specific object
		 *   containing the editor on which this widget will be
		 *   rendered. It contains as well some data of the editor's
		 *   current state (e.g., state, dispatch and preventBlur) that
		 *   is used to interpret an item selection
		 */
		render: (view) => {
			const items = Promise.resolve(options.items).then((allItems) => {
				resolvedItems = allItems;
				allItems.forEach(ensureItemLabel);
				return allItems;
			});

			const widget = makeDropdown({
				...options,
				onSelection: (item) => item.run(view.state, view.dispatch),
				onMouseDown: () => (view.preventBlur = true),
				title: options.label,
				items,
			});

			widget.appendTo(view.dom);

			return {
				// ProseMirror requires the dom element
				dom: widget.asJQuery().get(0),
				update: (state) => {
					if (resolvedItems) {
						resolvedItems.forEach((item) => item.update?.(state));
						widget.update();
					}
					return true;
				},
			};
		},
	});
}

function ensureItemLabel(item) {
	if (item.label) return;
	item.label = makeIcon({ title: item.title, ...item.iconSpecs });
}

function makeIcon({ klass, title, text = "" }) {
	return (html) => html.div({ klass, title }, text);
}
